/**
 * User Guide — the narrative content that is genuinely editorial.
 *
 * Everything mechanical (the list of dashboard sections, the rep tabs,
 * the buckets, the visit outcomes, the control states) is imported from
 * the product's own catalogs so it cannot drift. This module holds only
 * the parts a person has to write: the orientation chapter, the access
 * model, the FAQ, and the document's revision history.
 *
 * ── Maintaining this guide ────────────────────────────────────────────
 * 1. Product change → update the catalog it lives in
 *    (`depletionsSections.ts`, `regionalSalesGuide.ts`,
 *    `guideGlossary.ts`). The guide picks it up automatically.
 * 2. Narrative change → edit this file.
 * 3. Either way, add a row to `GUIDE_REVISIONS` and bump
 *    `GUIDE_VERSION`.
 * 4. Run `npm run guide:capture` to refresh screenshots and rebuild
 *    the downloadable PDF.
 */

/** Semantic version of the *document*, not of the application. */
export const GUIDE_VERSION = '1.3.0';

// ---------------------------------------------------------------------
// Revision history
// ---------------------------------------------------------------------

export interface GuideRevision {
  version: string;
  /** ISO date, YYYY-MM-DD. */
  date: string;
  /** Who signed off on the change. */
  author: string;
  /** What changed, one line per item. */
  changes: string[];
}

/** Newest first. The first entry is treated as the current revision. */
export const GUIDE_REVISIONS: GuideRevision[] = [
  {
    version: '1.3.0',
    date: '2026-09-03',
    author: 'Platform team',
    changes: [
      'Access and roles becomes Authentication & Authorization, in two named halves: who you are, and what that lets you reach.',
      'Explains the automatic sign-out after twenty minutes of inactivity, and that sessions are short and renew while you work.',
      'Collects accounts, passwords and sessions in one chapter instead of three places.',
    ],
  },
  {
    version: '1.2.0',
    date: '2026-09-02',
    author: 'Platform team',
    changes: [
      'Adds Announcements to the administrator capabilities — what the team sees on sign-in is now written and published from the admin area.',
    ],
  },
  {
    version: '1.1.0',
    date: '2026-09-02',
    author: 'Platform team',
    changes: [
      'Documents the No Shipments Yet tab — accounts on file we have never sold to.',
      'The state filter now lists every state, not only your own, for reps working away from home.',
    ],
  },
  {
    version: '1.0.0',
    date: '2026-09-02',
    author: 'Platform team',
    changes: [
      'First published edition.',
      'Covers Depletions, Regional Sales, and Regional Sales Oversight.',
      'Adds a glossary, an access-and-roles chapter, and a downloadable PDF.',
    ],
  },
];

// ---------------------------------------------------------------------
// Orientation — the first chapter a new user reads
// ---------------------------------------------------------------------

export interface GuideParagraph {
  heading?: string;
  body: string;
  bullets?: string[];
}

export const INTRODUCTION: GuideParagraph[] = [
  {
    body: 'Almost everything here is built on **depletions**: cases leaving a distributor’s warehouse and going to a retail account. That is the honest measure of demand — not what a distributor bought from us, which can sit in a warehouse for months.',
  },
  {
    heading: 'A few things to know up front',
    body: '',
    bullets: [
      'Volume is always shown in **6-packs**. The data arrives in 9-litre equivalent cases and the dashboard converts it for you.',
      'Depletions are reported **by month**, so an "order" here means an account’s total for that month — not a single purchase order with a date on it.',
      'New data is loaded **whenever a fresh file comes in**, so the latest month is usually still filling in. The Depletions page shows the date the data runs through.',
    ],
  },
  {
    body: 'Everything in the platform is internal and commercially sensitive. It is not shared outside the company.',
  },
];

export const AREA_OVERVIEW: {
  name: string;
  path: string;
  role: string;
  audience: string;
  summary: string;
}[] = [
  {
    name: 'Depletions',
    path: '/sales/depletions',
    role: 'Depletions',
    audience: 'Leadership, sales management, anyone analysing performance',
    summary: 'What sold, where, through which channel, and which way it is trending.',
  },
  {
    name: 'Regional Sales',
    path: '/regional-sales',
    role: 'Regional Sales Representative',
    audience: 'Field representatives',
    summary:
      'A representative’s daily workspace: who to call today, and the record of every visit.',
  },
  {
    name: 'Regional Sales Oversight',
    path: '/admin/regional-sales',
    role: 'Administrator',
    audience: 'Sales managers and administrators',
    summary:
      'The management view of the field team: who covers what, and everything they have logged.',
  },
];

/**
 * One-line framing under a chapter heading, for the chapters that need
 * it. Kept here rather than inline in the page so every word of the
 * guide lives in the data layer — which is also what lets the reading
 * estimate count all of it.
 */
export const CHAPTER_INTROS: Record<string, string> = {
  depletions:
    'The page opens as a list of named sections, grouped into five stages. Only the headline figures are open by default — click a section to expand it.',
  'regional-sales':
    'The representative’s own workspace — five tabs, built to be used on a phone in front of an account.',
  oversight:
    'The management counterpart to the rep workspace: the roster of representatives on the left, everything they have logged on the right.',
};

// ---------------------------------------------------------------------
// Getting started
// ---------------------------------------------------------------------

export interface GuideStep {
  title: string;
  body: string;
}

export const GETTING_STARTED: GuideStep[] = [
  {
    title: 'Get an account',
    body: 'An administrator creates your account and the platform emails you an invitation link to set your own password. You can also request access yourself from the sign-in screen, but the account stays pending until an administrator approves it.',
  },
  {
    title: 'Sign in',
    body: 'Use your work email and the password you set. Everything else about accounts, passwords and how long you stay signed in is in the **Signing in** chapter.',
  },
  {
    title: 'Pick a section',
    body: 'You land on a catalog of cards and see only the sections your roles allow. If one you expect is missing, you are missing that role — not looking in the wrong place.',
  },
  {
    title: 'Fill in your profile',
    body: 'Open Account Settings from the menu under your name. Representatives should add a home address: it is what lets Today’s Plan prefer accounts near you.',
  },
];

// ---------------------------------------------------------------------
// Authentication & Authorization — the AUTHORIZATION half
//
// Roles, what each grants, and what administrators can do. The
// authentication half (accounts, passwords, sessions) is further down,
// under AUTH_TOPICS.
// ---------------------------------------------------------------------

export interface RoleDoc {
  /** Display name as it appears in the admin role picker. */
  displayName: string;
  /** The underlying identifier, useful when reading audit entries. */
  name: string;
  grants: string;
}

export const ROLES: RoleDoc[] = [
  {
    displayName: 'Administrator',
    name: 'admin',
    grants:
      'Full platform access. Manages users, roles, and configuration, and opens Regional Sales Oversight. Administrators do not automatically get a rep workspace — tick Regional Sales Representative as well if the person also works accounts.',
  },
  {
    displayName: 'Depletions',
    name: 'depletions',
    grants: 'Opens the Depletions dashboard and all of its sections.',
  },
  {
    displayName: 'Regional Sales Representative',
    name: 'field_rep',
    grants:
      'Opens the rep workspace: Today’s Plan, My Territories, Manually Added Accounts, and My Logs. Logs visit notes, pins follow-ups, and sees the accounts in the states assigned to them.',
  },
  {
    displayName: 'Marketing',
    name: 'marketing',
    grants: 'Opens the Marketing intelligence sections. Outside the scope of this guide.',
  },
];

/**
 * The administrator surfaces. Documented here because the Access chapter
 * is where a reader asks "who can do what" — and until now the guide
 * described the roles without ever saying what the admin area contains.
 */
export const ADMIN_CAPABILITIES: { name: string; path: string; summary: string }[] = [
  {
    name: 'Users',
    path: '/admin/users',
    summary:
      'Create accounts, search the directory, change someone’s roles, disable or re-enable them, and approve or reject people who requested access themselves.',
  },
  {
    name: 'Roles',
    path: '/admin/roles',
    summary: 'See every role, what it grants, and who currently holds it.',
  },
  {
    name: 'Audit Log',
    path: '/admin/audit-log',
    summary:
      'Every administrative action in plain English, newest first — filterable by activity type or by person. Account flags raised by representatives land here too.',
  },
  {
    name: 'Announcements',
    path: '/admin/announcements',
    summary:
      'Write what the team sees when they sign in. Everything saves as a draft first, so nothing reaches anyone until you publish it — and unpublishing takes it back straight away. Shows who has seen each one and who has not.',
  },
  {
    name: 'Regional Sales Oversight',
    path: '/admin/regional-sales',
    summary:
      'The field team: who the representatives are, the territory each covers, and everything they have logged. Covered in Chapter 5.',
  },
];

export const ACCESS_NOTES: GuideParagraph[] = [
  {
    body: 'Roles are additive — a person can hold several. Team Chat and Account Settings need no role, so every active user can reach them. To change your roles, ask an administrator; it takes effect the next time you load the page.',
  },
  {
    heading: 'Administrator is a master key',
    body: 'The **Administrator** role satisfies every role check, in the app and in the API alike. An administrator reaches every section without being granted each role individually — so it is the one role to be deliberate about handing out. The exception is the representative workspace: an administrator can open it, but it stays empty until they also hold the Regional Sales Representative role and have a territory.',
  },
  {
    heading: 'What is recorded',
    body: 'Administrative actions — creating users, changing roles, resetting passwords, flagging accounts — go to an audit trail. Visit notes are visible to administrators and to anyone who opens that account.',
  },
];

// ---------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------

export const FEEDBACK_INTRO =
  'Every page has a **Feedback** button in the bottom-right corner. It is the fastest way to reach the team that builds this — faster than an email that has to find its way to the right person.';

export const FEEDBACK_NOTES: string[] = [
  'The page you were on is attached automatically, so there is no need to describe where you were.',
  'Leave the follow-up box ticked if you are happy to be contacted about it. Untick it to send anonymously.',
  'Something wrong in this guide counts too — a step that no longer matches the screen, or a term that is not explained. Pick **Bug** and say which chapter.',
];

// ---------------------------------------------------------------------
// FAQ
// ---------------------------------------------------------------------

export interface FaqEntry {
  question: string;
  answer: string;
}

/**
 * Authentication & Authorization — the AUTHENTICATION half.
 *
 * Accounts, passwords and sessions: proving who you are, and how long
 * that lasts. The authorization half — what the account may then reach —
 * is ROLES / ACCESS_NOTES / ADMIN_CAPABILITIES above.
 *
 * This used to be spread across Getting started, the access chapter and
 * the FAQ, which meant documenting one change to sessions took edits in
 * four places and was easy to leave half-done. It is one chapter now,
 * placed after the business chapters because it is the only technical
 * subject in a book about working a sales territory.
 */
export const AUTH_TOPICS: { heading: string; body: string }[] = [
  {
    heading: 'Getting an account',
    body: 'An administrator creates your account and the platform emails you an invitation link to set your own password. You can also request access yourself from the sign-in screen, but the account stays **pending** until an administrator approves it — so nobody reaches the data by signing themselves up.',
  },
  {
    heading: 'Your password',
    body: 'You choose it from the invitation link; nobody else ever sees it. **Forgot your password** on the sign-in screen emails you a reset link. If an administrator resets it for you, you are asked to choose a new one the moment you next sign in — the temporary state never lasts beyond that.',
  },
];

/**
 * The two ways a session ends.
 *
 * Structured rather than prose because the numbers are the point: a rep
 * wants to know how long they have, and a sentence buries that. Each
 * carries its own accent so the two are told apart at a glance.
 */
export const SESSION_ENDINGS: {
  trigger: string;
  /** The number that answers "how long do I have?". */
  timing: string;
  body: string;
  /** Semantic accent, resolved to a colour by the guide page. */
  tone: 'inactivity' | 'time';
}[] = [
  {
    trigger: 'Inactivity',
    timing: '20 minutes',
    tone: 'inactivity',
    body: 'After twenty minutes without activity you get a **one minute** warning, then you are signed out. Choose **Stay Signed In** to carry on. The clock runs whether or not this tab is the one you are looking at, so working in another tab will still sign you out here — the sign-in screen says so when it happens.',
  },
  {
    trigger: 'Time',
    timing: 'Renews as you work',
    tone: 'time',
    body: 'Sessions are short by design and renew quietly while you are working, so you will not notice them during the day. Coming back after a break or the next morning means signing in again.',
  },
];

/** Why the session ends rather than waiting to be closed. */
export const SESSION_RATIONALE =
  'This platform holds commercial data — what every account buys, and what the field team is doing about it. A signed-in screen somebody walked away from is the likeliest way that leaks, which is why the session ends on its own. Signing back in returns you to the page you were on, so the cost is a password, not your place.';

/** The one rule a reader must not get wrong. */
export const AUTH_CAUTION = {
  title: 'One account, one person',
  body: 'Never share a sign-in. Visits, notes and administrative actions are all recorded against whoever is signed in, so a shared account makes the record wrong for everyone — and losing one password would mean locking out several people at once. If a colleague needs access, an administrator can create them an account in a minute.',
};

export const FAQ: FaqEntry[] = [
  {
    question: 'It asked “Still there?” and signed me out.',
    answer:
      'After twenty minutes without activity we give you a minute’s warning and then sign you out, so a laptop left open in a bar or a customer’s back office is not left signed in. Choose “Stay Signed In” to carry on. The clock does not care which tab you are looking at — if you spend twenty minutes in another tab you will come back to the sign-in screen, and it will tell you that is what happened. Signing back in returns you to the page you were on.',
  },
  {
    question: 'A section I expect to see is missing from the home page.',
    answer:
      'You only see sections your roles allow. Ask an administrator to check which roles are on your account.',
  },
  {
    question: 'This month’s numbers look low.',
    answer:
      'Check the coverage range at the top of the Depletions page — it shows the date the data runs through. The latest month is usually still filling in, and completes as more data is loaded.',
  },
  {
    question: 'Why is an account’s volume negative?',
    answer:
      'Product was returned or pulled back. Negative rows are shown rather than hidden, so the totals reconcile.',
  },
  {
    question: 'I cannot find an account I visited in Today’s Plan.',
    answer:
      'Either it was added by hand and has no depletion history yet, so it sits in Manually Added Accounts; or you logged a visit there recently and it is still on cooldown; or it is outside your territory. It stays visible in My Territories either way.',
  },
  {
    question: 'What is the difference between a flag and a visit outcome?',
    answer:
      'An outcome describes how a conversation went and sets when the account comes back to you. A flag asks an administrator to correct the account record.',
  },
  {
    question: 'An account looks lost, but I know they still buy from us.',
    answer:
      'The buckets measure order recency only, so a twice-a-year buyer looks dormant in between. Check its history on the account page first.',
  },
];
